import { EnrichedPost } from '@frontend/applications/Social/Analytics/hooks/usePostsData';
import { ENTITY_TYPE_TO_POST_TYPE, PostType } from '@frontend/applications/Social/types';
import { AdvertisableMediaPermissions, ContentAdapter, ContentPreviewData } from '../types';

/**
 * Map entity network and type to the corresponding PostType
 */
const mapToPostType = (network: string, entityType?: string): PostType | undefined => {
  const normalizedNetwork = network.toUpperCase();

  const postType = ENTITY_TYPE_TO_POST_TYPE[entityType];
  if (postType) {
    return postType;
  }

  switch (normalizedNetwork) {
    case 'INSTAGRAM':
      if (entityType === 'REEL') return 'INSTAGRAM_REEL';
      if (entityType === 'STORY') return 'INSTAGRAM_STORY';
      return 'INSTAGRAM_POST';
    case 'PINTEREST':
      return 'PINTEREST_PIN';
    case 'TIKTOK':
      return 'TIKTOK_VIDEO';
    case 'YOUTUBE':
      if (entityType === 'SHORT') return 'YOUTUBE_SHORT';
      return 'YOUTUBE_VIDEO';
    default:
      return undefined;
  }
};

/**
 * Adapter to convert PostInsights data to ContentPreviewData format
 */

export const postAdapter: ContentAdapter<EnrichedPost> = (
  post: EnrichedPost,
  options?: { startDate?: string; endDate?: string },
): ContentPreviewData => {
  const startDate = options?.startDate;
  const endDate = options?.endDate;

  // Determine platform based on entityNetwork
  const platform = post.entityNetwork.toLowerCase() as ContentPreviewData['metadata']['platform'];

  // Determine content type (default to image if not specified)
  const contentType = post.entityType === 'VIDEO' ? 'video' : post.entityType === 'CAROUSEL' ? 'carousel' : 'image';

  // Map to appropriate post type
  const postType = mapToPostType(post.entityNetwork, post.entityType);

  // Convert dates to strings
  const createdAt = post.postDate
    ? typeof post.postDate === 'string'
      ? post.postDate
      : post.postDate.toISOString()
    : new Date().toISOString();

  const updatedAt = post.postUpdateDate
    ? typeof post.postUpdateDate === 'string'
      ? post.postUpdateDate
      : post.postUpdateDate.toISOString()
    : undefined;
  return {
    id: post.entityId,
    createdAt,
    updatedAt,
    mentionId: post.mentionId,
    creator: {
      id: post.memberId,
      name: post.member?.name || 'Unknown',
      handle: post.accountUsername || 'unknown',
      network: platform,
      avatarUrl: post.member?.profilePicture || undefined,
    },
    metrics: {
      impressions: post.total?.impressions || 0,
      engagement: post.total?.engagements || 0,
      engagementRate: post.total?.engagementRate || 0,
      tmv: post.total?.tmv || undefined,
      metadata: {
        startDate,
        endDate,
      },
    },
    metadata: {
      platform,
      contentType,
      postType,
      project: post.project ?? undefined,
      group: post.group ?? undefined,
    },
    caption: post.postCaption ?? '', // Caption field not available in PostInsights
    mediaUrl: post.postImageUrl || '', // Use post image URL as media URL
    originalUrl: post.postPermalink || '', // Use permalink as original URL
    postExternalId: post.postExternalId ?? undefined,
    advertisableMediaPermissions: post.advertisableMediaPermissions?.filter(Boolean) as
      | AdvertisableMediaPermissions[]
      | undefined,
  };
};

export default postAdapter;
